import React, { useEffect, useState } from 'react';
import { aiProvidersService } from '../services/apiService';
import { AIProvider, AIProviderFormData } from '../types';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FormField } from '../components/ui/FormField';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Textarea } from '../components/ui/textarea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useToast } from '../contexts/ToastContext';
import { MasterDataImportExport } from '../components/admin/MasterDataImportExport';

interface EditModalProps {
  provider: AIProvider | null;
  onSave: (data: Partial<AIProviderFormData>) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const EditModal: React.FC<EditModalProps> = ({ provider, onSave, onClose, isOpen }) => {
  const [formData, setFormData] = useState<Partial<AIProviderFormData>>({
    name: '',
    providerType: 'openai',
    description: '',
    baseUrl: '',
    defaultModel: 'gpt-4',
    status: 'active',
    apiKey: '',
    costPer1kTokens: 0.002,
    costPerRequest: 1,
    rateLimitPerMinute: 60,
    rateLimitPerHour: 1000,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (provider) {
      setFormData({
        name: provider.name || '',
        providerType: provider.providerType || 'openai',
        description: provider.description || '',
        baseUrl: provider.baseUrl || '',
        defaultModel: provider.defaultModel || 'gpt-4',
        status: provider.status || 'active',
        apiKey: '', // Never show existing API key
        costPer1kTokens: provider.costPer1kTokens || 0.002,
        costPerRequest: provider.costPerRequest || 1,
        rateLimitPerMinute: provider.rateLimitPerMinute || 60,
        rateLimitPerHour: provider.rateLimitPerHour || 1000,
      });
    } else {
      setFormData({
        name: '',
        providerType: 'openai',
        description: '',
        baseUrl: '',
        defaultModel: 'gpt-4',
        status: 'active',
        apiKey: '',
        costPer1kTokens: 0.002,
        costPerRequest: 1,
        rateLimitPerMinute: 60,
        rateLimitPerHour: 1000,
      });
    }
  }, [provider]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.defaultModel) {
        throw new Error('Name and Default Model are required');
      }

      // For create, API key is required
      if (!provider && !formData.apiKey) {
        throw new Error('API Key is required for new providers');
      }

      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save AI Provider');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-2xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {provider ? 'Edit AI Provider' : 'Create AI Provider'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              label="Name"
              required
            >
              <Input
                value={formData.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., OpenAI Production"
              />
            </FormField>

            <FormField
              label="Provider Type"
              required
            >
              <Select
                value={formData.providerType || 'openai'}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, providerType: e.target.value as any })}
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic (Claude)</option>
                <option value="google">Google (Gemini)</option>
                <option value="custom">Custom AI API</option>
              </Select>
            </FormField>

            <FormField
              label="Description"
            >
              <Textarea
                value={formData.description || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this provider"
                rows={3}
              />
            </FormField>

            <div className="relative">
              <FormField
                label="API Key"
                required={!provider}
                helpText={provider ? "Leave blank to keep existing key" : "API key will be encrypted"}
              >
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={formData.apiKey || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder={provider ? "Leave blank to keep existing key" : "Enter API key"}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  {showApiKey ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0L3 3m3.59 3.59L3 3m0 0l3.59 3.59M3 3l3.59 3.59m0 0L3 3" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </FormField>
            </div>

            <FormField
              label="Base URL (Optional)"
              helpText="Custom base URL if different from default"
            >
              <Input
                value={formData.baseUrl || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, baseUrl: e.target.value })}
                placeholder="Leave empty for default provider URLs"
              />
            </FormField>

            <FormField
              label="Default Model"
              required
            >
              <Input
                value={formData.defaultModel || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, defaultModel: e.target.value })}
                placeholder="e.g., gpt-4, claude-3-opus, gemini-pro"
              />
            </FormField>

            <FormField
              label="Status"
            >
              <Select
                value={formData.status || 'active'}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="testing">Testing</option>
              </Select>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Cost per Request (Int Coins)"
              >
                <Input
                  type="number"
                  value={formData.costPerRequest || 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, costPerRequest: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </FormField>

              <FormField
                label="Cost per 1k Tokens (Int Coins)"
              >
                <Input
                  type="number"
                  value={formData.costPer1kTokens || 0}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, costPer1kTokens: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.0001"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Rate Limit (per minute)"
              >
                <Input
                  type="number"
                  value={formData.rateLimitPerMinute || 60}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, rateLimitPerMinute: parseInt(e.target.value) || 60 })}
                  min="1"
                />
              </FormField>

              <FormField
                label="Rate Limit (per hour)"
              >
                <Input
                  type="number"
                  value={formData.rateLimitPerHour || 1000}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, rateLimitPerHour: parseInt(e.target.value) || 1000 })}
                  min="1"
                />
              </FormField>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : provider ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const AdminAIProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('all');
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      const data = await aiProvidersService.listProviders();
      setProviders(data);
    } catch (error: any) {
      showToast('Failed to load AI providers', 'error');
      console.error('Error loading providers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProvider(null);
    setIsModalOpen(true);
  };

  const handleEdit = (provider: AIProvider) => {
    setEditingProvider(provider);
    setIsModalOpen(true);
  };

  const handleSave = async (data: Partial<AIProviderFormData>) => {
    try {
      if (editingProvider) {
        await aiProvidersService.updateProvider(editingProvider.id, data);
        showToast('AI Provider updated successfully', 'success');
      } else {
        await aiProvidersService.createProvider(data);
        showToast('AI Provider created successfully', 'success');
      }
      await loadProviders();
      setIsModalOpen(false);
      setEditingProvider(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save AI Provider';
      throw new Error(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this AI Provider? This action cannot be undone.')) {
      return;
    }

    try {
      await aiProvidersService.deleteProvider(id);
      showToast('AI Provider deleted successfully', 'success');
      await loadProviders();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to delete AI Provider', 'error');
    }
  };

  const handleToggleStatus = async (provider: AIProvider) => {
    try {
      if (provider.status === 'active') {
        await aiProvidersService.deactivateProvider(provider.id);
        showToast('AI Provider deactivated', 'success');
      } else {
        await aiProvidersService.activateProvider(provider.id);
        showToast('AI Provider activated', 'success');
      }
      await loadProviders();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.defaultModel.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
    const matchesType = providerTypeFilter === 'all' || provider.providerType === providerTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 dark:bg-green-900/30 text-green-800', icon: '●' },
      inactive: { color: 'bg-red-100 dark:bg-red-900/30 text-red-800', icon: '●' },
      testing: { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800', icon: '⚠' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    
    return (
      <Badge className={config.color}>
        <span className="mr-1">{config.icon}</span>
        {status}
      </Badge>
    );
  };

  const getProviderTypeBadge = (type: string) => {
    const typeConfig: Record<string, { color: string; label: string }> = {
      openai: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800', label: 'OpenAI' },
      anthropic: { color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800', label: 'Claude' },
      google: { color: 'bg-red-100 dark:bg-red-900/30 text-red-800', label: 'Gemini' },
      custom: { color: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100', label: 'Custom' },
    };
    const config = typeConfig[type] || typeConfig.custom;
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <PageHeader
        title="AI Providers Management"
        subtitle="Manage AI providers for AI summary generation"
        actionButtonText="Add AI Provider"
        onActionButtonClick={handleCreate}
        actionButtonIcon={
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        }
      >
        <MasterDataImportExport resource="ai_providers" onComplete={loadProviders} />
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Search"
            >
              <Input
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                placeholder="Search by name, description, or model..."
              />
            </FormField>

            <FormField
              label="Status"
            >
              <Select
                value={statusFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="testing">Testing</option>
              </Select>
            </FormField>

            <FormField
              label="Provider Type"
            >
              <Select
                value={providerTypeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setProviderTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                <option value="custom">Custom</option>
              </Select>
            </FormField>
          </div>
        </Card>

        {/* Providers List */}
        <Card>
          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProviders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No AI providers found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  filteredProviders.map((provider) => (
                    <tr key={provider.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{provider.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{provider.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getProviderTypeBadge(provider.providerType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{provider.defaultModel}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(provider.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {(provider.costPerRequest || 0)} + {(provider.costPer1kTokens || 0)}/1k tokens
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <div>{(provider.totalRequests || 0)} requests</div>
                          <div className="text-gray-500 dark:text-gray-400">{(provider.totalTokensUsed || 0).toLocaleString()} tokens</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleToggleStatus(provider)}
                            className={`text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 ${
                              provider.status === 'active' ? '' : 'text-green-600 dark:text-green-400 hover:text-green-900'
                            }`}
                          >
                            {provider.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleEdit(provider)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(provider.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredProviders.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-200">
                Showing <span className="font-medium">{filteredProviders.length}</span> of{' '}
                <span className="font-medium">{providers.length}</span> providers
              </div>
            </div>
          )}
        </Card>
      </div>

      <EditModal
        provider={editingProvider}
        onSave={handleSave}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProvider(null);
        }}
        isOpen={isModalOpen}
      />
    </div>
  );
};

